import * as React from "react";
import { UIModel } from "../../model/ui-model";
import { Icon, Spinner } from "@amzn/awsui-components-react";
import { AppContext } from "../../setup/context";
import { EntityRef } from "@amzn/ask-legal-domain";
import { callApi } from "../../utils/api-call";
import "../../styles/component/favor-button-v2.scss";

/**
 * Favorite toggle for the PathFinder Agentic UI lab. Same favor/unfavor behavior as
 * FavorButton, but keyboard and screen-reader accessible (native toggle button with
 * aria-pressed). Uses a Cloudscape Icon, which takes the surrounding text colour when
 * not favorited and the PathFinder orange accent when favorited.
 * - size "normal": 16px icon, used in container headers.
 * - size "big": 32px icon, used next to the page title.
 */
export const FavorButtonV2 = (props: {
    isFavoritedState: UIModel.State<boolean>;
    targetEntityRef: EntityRef;
    /** What is being favorited, used in the accessible label, e.g. "page" or "container" */
    entityLabel: string;
    size?: "normal" | "big";
}) => {
    const context = React.useContext(AppContext);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    // Avoid updating state if the button unmounts while a request is in flight
    const isMountedRef = React.useRef<boolean>(true);

    React.useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);

    const isFavorited = !!props.isFavoritedState.value;
    const size = props.size || "normal";

    const toggleFavorited = async () => {
        const shouldFavor = !isFavorited;
        const userProfileAPI = context.getUserProfileAPI();
        setIsSaving(true);
        // Same as FavorButton: errors are not shown, the toggle just keeps its previous state
        const result = await callApi(
            shouldFavor ? userProfileAPI.favor : userProfileAPI.unfavor,
            props.targetEntityRef
        );
        if (!isMountedRef.current) return;
        setIsSaving(false);
        if (result.ok) {
            props.isFavoritedState.setValue(shouldFavor);
        }
    };

    return (
        <button
            type="button"
            className={`pf-favor-toggle${isFavorited ? " pf-favor-toggle--favorited" : ""}`}
            aria-pressed={isFavorited}
            aria-label={isFavorited
                ? `Remove ${props.entityLabel} from favorites`
                : `Add ${props.entityLabel} to favorites`}
            disabled={isSaving}
            onClick={toggleFavorited}
        >
            {isSaving
                ? <Spinner size={size} />
                : <Icon name={isFavorited ? "heart-filled" : "heart"} size={size} />}
        </button>
    );
};
